import { useEffect, useMemo } from "react";
import { useCompanySettingsQuery } from "modules/company/hooks/useCompanySettingsQuery";
import { ICompany } from "modules/company/types";
import { useEventContext } from "modules/eventLanding/hooks/useEventContext";
import { selectShowTickets } from "modules/ticketing/redux/selectors";
import { useSelector } from "react-redux";
import { Dialog } from "@remo-co/ui-core/src/components/Dialog";
import { useAppDispatch } from "store/hooks";
import { setShowTickets } from "modules/ticketing/redux/slice";
import { getUserName } from "modules/userProfile";
import IframeResizer from "iframe-resizer-react";
import {
  selectIsAnonymousUser,
  selectUser,
} from "modules/auth/redux/selectors";
import { isUsersNameMissing } from "modules/auth/components/MissingFullNameDialog";
import { addInfoNotification } from "modules/notification/redux/notificationSlice";
import useAuth from "modules/auth/hooks/useAuth";
import { setShowRegisterDialog } from "modules/eventLanding/redux/eventLandingSlice";
import { useI18n } from "i18n";
import zIndexes from "zIndexes.module.scss";
import { useStyles } from "./styles";

export const BuyTickets = () => {
  const styles = useStyles();
  const dispatch = useAppDispatch();
  const showTickets = useSelector(selectShowTickets);
  const { event, isEventDataLoading } = useEventContext();
  const user = useSelector(selectUser);
  const eventCompanyId = event && (event.company as ICompany)?._id;
  const { companySettings, status } = useCompanySettingsQuery(eventCompanyId);
  const eventcubeEventId = event?.eventcubeEventId;
  const eventcubeSlug = event?.eventcubeEventSlug;
  const { signOut } = useAuth();
  const eventcubeStoreId = companySettings?.eventcubeSettings?.storeId;
  const isUserAnonymous = useSelector(selectIsAnonymousUser);
  const { t } = useI18n("eventLanding");

  const handleClose = () => {
    dispatch(setShowTickets(false));
    dispatch(setShowRegisterDialog(false));
  };

  useEffect(() => {
    const logoutIfAnonymous = async () => {
      if (showTickets && isUserAnonymous) {
        dispatch(
          addInfoNotification({
            message: t("join.in.with.email"),
          }),
        );

        await signOut();
        dispatch(setShowTickets(false));
      }
    };
    logoutIfAnonymous();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isUserAnonymous, showTickets, t]);

  const src = useMemo(() => {
    if (!user || !event) return "";

    return `https://${eventcubeStoreId}.remotickets.co/events/${eventcubeEventId}/${eventcubeSlug}/?embed=true&prefill=${encodeURIComponent(
      JSON.stringify({
        name: getUserName(user),
        email: user.email,
      }),
    )}&checkout_data=${encodeURIComponent(
      JSON.stringify({
        remoUserId: user.id,
        remoEventId: event._id,
      }),
    )}&success_redirect_url=${encodeURIComponent(
      `${window.location.origin}/e/${event.code}/register`,
    )}`;
  }, [event, eventcubeEventId, eventcubeSlug, eventcubeStoreId, user]);

  if (
    !user ||
    !event ||
    status === "pending" ||
    isEventDataLoading ||
    isUsersNameMissing(user)
  ) {
    return null;
  }

  if (showTickets) {
    return (
      <Dialog
        style={{ zIndex: zIndexes.ticketsModalZIndex }}
        onClose={handleClose}
        fullScreen={false}
        open={showTickets}
        data-testid="buy-tickets-dialog"
      >
        <div className={styles.wrapper} id={`ecEmbed_${eventcubeEventId}`}>
          <IframeResizer
            id={`ecEmbedIframe_${eventcubeEventId}`}
            src={src}
            className={styles.iframe}
            checkOrigin={false}
          />
        </div>
      </Dialog>
    );
  }

  return null;
};
